/**
 * McKee's product-page purchase information.
 *
 * Runs from a dedicated deferred asset so the delivery date and Klarna modal
 * also initialize reliably inside Shopify theme previews and section reloads.
 */
(function () {
  'use strict';

  const refreshKey = 'mckPurchaseInformationRefresh';

  if (window.mckPurchaseInformationAssetInitialized) {
    window[refreshKey]?.(document);
    return;
  }

  window.mckPurchaseInformationAssetInitialized = true;

  const languageFor = (element) => (
    String(element?.dataset?.locale || document.documentElement.lang || 'it').toLowerCase().startsWith('en') ? 'en' : 'it'
  );

  const formatMoney = (cents, currency, language = 'it') => new Intl.NumberFormat(language === 'en' ? 'en-IE' : 'it-IT', {
    style: 'currency',
    currency: currency || 'EUR'
  }).format(cents / 100);

  const dateKey = (date) => (
    `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`
  );

  const easterSunday = (year) => {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;

    return new Date(Date.UTC(year, month - 1, day, 12));
  };

  const italianHolidayKeys = (year) => {
    const fixedHolidays = [
      [1, 1],
      [1, 6],
      [4, 25],
      [5, 1],
      [6, 2],
      [8, 15],
      [11, 1],
      [12, 8],
      [12, 25],
      [12, 26]
    ];

    if (year >= 2026) fixedHolidays.push([10, 4]);

    const holidays = new Set(
      fixedHolidays.map(([month, day]) => (
        `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      ))
    );
    const easterMonday = easterSunday(year);
    easterMonday.setUTCDate(easterMonday.getUTCDate() + 1);
    holidays.add(dateKey(easterMonday));

    return holidays;
  };

  const isBusinessDay = (date) => {
    const day = date.getUTCDay();
    return day !== 0 && day !== 6 && !italianHolidayKeys(date.getUTCFullYear()).has(dateKey(date));
  };

  const nextBusinessDay = (date) => {
    const candidate = new Date(date);

    do {
      candidate.setUTCDate(candidate.getUTCDate() + 1);
    } while (!isBusinessDay(candidate));

    return candidate;
  };

  const animateDeliveryTruck = (element) => {
    if (element.dataset.mckDeliveryAnimated === 'true') return;

    element.dataset.mckDeliveryAnimated = 'true';

    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    const truck = element.querySelector('.mck-delivery-estimate__truck');
    if (!truck) return;

    element.classList.add('is-driving-in');
    truck.addEventListener('animationend', (event) => {
      if (event.animationName === 'mck-truck-drive-once') {
        element.classList.remove('is-driving-in');
      }
    }, { once: true });
  };

  const renderDeliveryEstimate = (element) => {
    try {
      const workingDays = Math.max(1, Number.parseInt(element.dataset.workingDays || '4', 10));
      const cutoffHour = Math.min(23, Math.max(0, Number.parseInt(element.dataset.cutoffHour || '12', 10)));
      const timeZone = element.dataset.timeZone || 'Europe/Rome';
      const language = languageFor(element);
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23'
      }).formatToParts(new Date()).reduce((result, part) => {
        if (part.type !== 'literal') result[part.type] = Number.parseInt(part.value, 10);
        return result;
      }, {});

      let processingDate = new Date(Date.UTC(parts.year, parts.month - 1, parts.day, 12));
      const orderedAfterCutoff = parts.hour >= cutoffHour;
      const orderDateIsBusinessDay = isBusinessDay(processingDate);

      if (orderedAfterCutoff || !orderDateIsBusinessDay) {
        processingDate = nextBusinessDay(processingDate);
      }

      const deliveryDate = new Date(processingDate);
      let addedWorkingDays = 0;

      while (addedWorkingDays < workingDays) {
        deliveryDate.setUTCDate(deliveryDate.getUTCDate() + 1);

        if (isBusinessDay(deliveryDate)) {
          addedWorkingDays += 1;
        }
      }

      const dateElement = element.querySelector('[data-mck-delivery-date]');
      const cutoffElement = element.querySelector('[data-mck-delivery-cutoff]');
      const includeYear = deliveryDate.getUTCFullYear() !== parts.year;
      const formattedDate = new Intl.DateTimeFormat(language === 'en' ? 'en-GB' : 'it-IT', {
        timeZone: 'UTC',
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        ...(includeYear ? { year: 'numeric' } : {})
      }).format(deliveryDate);

      if (dateElement) dateElement.textContent = language === 'en' ? `by ${formattedDate}` : `entro ${formattedDate}`;

      if (cutoffElement) {
        cutoffElement.textContent = !orderedAfterCutoff && orderDateIsBusinessDay
          ? (language === 'en'
            ? `Order by ${String(cutoffHour).padStart(2, '0')}:00`
            : `Ordina entro le ${String(cutoffHour).padStart(2, '0')}:00`)
          : (language === 'en'
            ? 'Order now: processing starts the next working day'
            : 'Ordina ora: elaborazione dal prossimo giorno lavorativo');
      }

      element.dataset.mckDeliveryRendered = 'true';
    } catch (error) {
      const cutoffElement = element.querySelector('[data-mck-delivery-cutoff]');
      if (cutoffElement) {
        cutoffElement.textContent = languageFor(element) === 'en'
          ? 'Confirm the exact date at checkout'
          : 'Verifica la data esatta al checkout';
      }
      element.dataset.mckDeliveryRendered = 'fallback';
    }

    animateDeliveryTruck(element);
  };

  const updateKlarnaPrice = (container, price) => {
    const priceCents = Number.parseInt(price, 10);
    if (!Number.isFinite(priceCents) || priceCents <= 0) return;

    const currency = container.dataset.currency || 'EUR';
    const language = languageFor(container);
    const installmentCents = Math.floor(priceCents / 3);
    container.dataset.price = String(priceCents);
    container.dataset.mckKlarnaPriceInitialized = 'true';

    container.querySelectorAll('[data-mck-klarna-installment], [data-mck-klarna-dialog-installment]').forEach((element) => {
      element.textContent = formatMoney(installmentCents, currency, language);
    });

    container.querySelectorAll('[data-mck-klarna-total]').forEach((element) => {
      element.textContent = formatMoney(priceCents, currency, language);
    });

    container.querySelectorAll('[data-mck-klarna-rounding-note]').forEach((element) => {
      element.hidden = priceCents % 3 === 0;
    });

    const paypalEnabled = container.dataset.paypalEnabled === 'true';
    const paypalMin = Number.parseInt(container.dataset.paypalMin || '2000', 10);
    const paypalMax = Number.parseInt(container.dataset.paypalMax || '300000', 10);
    const paypalEligibleAmount = paypalEnabled && priceCents >= paypalMin && priceCents <= paypalMax;

    container.querySelectorAll('[data-mck-paypal-provider]').forEach((element) => {
      element.hidden = !paypalEligibleAmount;
    });

    container.querySelectorAll('[data-mck-payment-providers]').forEach((element) => {
      element.textContent = paypalEligibleAmount
        ? (language === 'en' ? 'Klarna or PayPal' : 'Klarna o PayPal')
        : 'Klarna';
    });
  };

  const localizeReassurance = (root = document) => {
    if (!String(document.documentElement.lang || '').toLowerCase().startsWith('en')) return;

    elementsWithin(root, '.impact-pdp-reassurance').forEach((row) => {
      row.setAttribute('aria-label', 'Purchase assurances');
      const labels = ['15-day money-back guarantee', 'Free returns', 'Delivery in 3–5 working days'];
      row.querySelectorAll('.impact-pdp-reassurance__item span').forEach((label, index) => {
        if (labels[index]) label.textContent = labels[index];
      });
    });
  };

  const updateKlarnaFallbacks = () => {
    document.querySelectorAll('[data-mck-klarna-message]').forEach((container) => {
      const fallback = container.querySelector('[data-mck-klarna-fallback]');
      const officialPlacement = container.querySelector('klarna-placement');

      if (fallback) fallback.hidden = Boolean(officialPlacement);

      if (container.dataset.mckKlarnaPriceInitialized !== 'true') {
        updateKlarnaPrice(container, container.dataset.price);
      }
    });
  };

  const elementsWithin = (root, selector) => {
    const elements = [];

    if (root instanceof Element && root.matches(selector)) elements.push(root);
    if (root?.querySelectorAll) elements.push(...root.querySelectorAll(selector));

    return elements;
  };

  const refresh = (root = document) => {
    elementsWithin(root, '[data-mck-klarna-message]').forEach((container) => {
      updateKlarnaPrice(container, container.dataset.price);
    });
    elementsWithin(root, 'mck-delivery-estimate').forEach(renderDeliveryEstimate);
    localizeReassurance(root);
    updateKlarnaFallbacks();
  };

  window[refreshKey] = refresh;

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest?.('[data-mck-klarna-trigger]');

    if (trigger) {
      const dialog = trigger.closest('[data-mck-klarna-message]')?.querySelector('[data-mck-klarna-dialog]');
      if (dialog?.showModal) dialog.showModal();
    }

    if (event.target.matches?.('[data-mck-klarna-dialog]')) {
      event.target.close();
    }
  });

  document.addEventListener('variant:change', (event) => {
    const purchaseAmount = event.detail?.variant?.price;
    const sourceSection = event.target instanceof Element
      ? event.target.closest('.shopify-section')
      : null;
    const containers = sourceSection
      ? sourceSection.querySelectorAll('[data-mck-klarna-message]')
      : document.querySelectorAll('[data-mck-klarna-message]');

    if (purchaseAmount) {
      containers.forEach((container) => updateKlarnaPrice(container, purchaseAmount));
    }

    if (purchaseAmount && window.KOSMApp?.updatePurchaseAmount) {
      window.KOSMApp.updatePurchaseAmount(purchaseAmount);
    }
  });

  document.addEventListener('shopify:section:load', (event) => refresh(event.target));

  let refreshQueued = false;
  const observer = new MutationObserver((mutations) => {
    const hasRelevantAddition = mutations.some((mutation) => (
      Array.from(mutation.addedNodes).some((node) => (
        node instanceof Element && (
          node.matches('mck-delivery-estimate, [data-mck-klarna-message], klarna-placement')
          || node.querySelector('mck-delivery-estimate, [data-mck-klarna-message], klarna-placement')
          || node.closest('[data-mck-klarna-message]')
        )
      ))
    ));

    if (!hasRelevantAddition || refreshQueued) return;

    refreshQueued = true;
    queueMicrotask(() => {
      refreshQueued = false;
      refresh(document);
    });
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => refresh(document), { once: true });
  } else {
    refresh(document);
  }
})();
